import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import axios from 'axios';
import { toaster } from 'app/utilities/commons';
import * as Yup from 'yup';
import { Account } from 'app/types/account';
const API_URL = process.env.REACT_APP_API_NET;

type Bank = {
  id: number;
  description: string;
};

type FormikValues = {
  account: {
    bankId: number;
    name: string;
    account: string;
  };
};

type DepositAccountsListProps = {
  collabId?: number;
};

const DepositAccountsList: React.FC<DepositAccountsListProps> = ({
  collabId,
}) => {

  const DepositAccountSchema = Yup.object().shape({
    account: Yup.object({
      // bankId: Yup.string().required('Este campo es requerido'),
      bankId: Yup.number().notOneOf([0],"Seleccione una opción").nullable(false).required("Este campo es requerido."),
      name: Yup.string().trim().min(4,"Mínimo 4 caracteres").required('Este campo es requerido'),
      account: Yup.string().min(10,"Mínimo 10 números").max(18,"Máximo 18 números").required('Este campo es requerido'),
    }).required("Este campo es requerido"),
  });

  const [banks, setBanks] = useState<Bank[]>([]);
  const [savedAccounts, setSavedAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  
  const formik = useFormik<FormikValues>({
    validationSchema: DepositAccountSchema,
    validateOnBlur: true,
    validateOnChange: true,
    validateOnMount: true,
    initialValues: {
      account: {
        bankId: 0,
        name: '',
        account: '',
      },
    },
    onSubmit: async (values) => {
      if (!collabId) return;

      // Verificar si la cuenta ya existe en `savedAccounts`
      const accountExists = savedAccounts.some(
        (acc) => acc.account === values.account.account
      );

      if (accountExists) {
        toast.error('Esta cuenta ya ha sido registrada.');
        return; // Evitar la duplicación
      }

      try {
        setLoading(true);
        const payload = {
          ...values.account,
          collaboratorId: collabId,
        };

        await axios.post(`${API_URL}AccountBank`, payload);

        toast.success('Cuenta agregada con éxito');
        formik.resetForm();
        fetchAccounts();
      } catch (error:any) {
        // toast.error('Error al guardar la cuenta');
        toast.error("Asegúrese que los campos no esten vacíos");
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchBanks = async () => {
    try {
      const response = await axios.get(`${API_URL}Bank?PageSize=1000`);
      const filteredBanks = (response.data.data.records ?? []).filter((item: any) => item.statusId === 1);
      setBanks(filteredBanks);
    } catch (error) {
      toast.error('Error al cargar bancos');
    }
  };

  const fetchAccounts = async () => {
    if (!collabId) return;

    try {
      const response = await axios.get(
        `${API_URL}AccountBank/${collabId}/collaborator`
      );
      setSavedAccounts(response.data.data ?? []);
    } catch (error) {
      setSavedAccounts([]);
    }
  };

  useEffect(() => {
    fetchBanks();
    if (collabId) fetchAccounts();
  }, [collabId]);

  const handleRadioChange = async (id: number) => {
    try {
      setDisabled(true);
      await axios.put(`${API_URL}AccountBank/${id}/status`, { id });
      fetchAccounts();
    } catch (err: any) {
      console.error('Error en cambio de status cuenta', err);
      toaster.error(err?.response?.data?.message ?? err.message);
    } finally {
      setDisabled(false);
    }
  };

  const handleAccountInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (/^\d*$/.test(value) && value.length <= 19) {
      formik.setFieldValue('account.account', value);
    }
  };

  return (
    <div className="table-responsive">
      <form onSubmit={formik.handleSubmit}>
        <table className="table table-border table-row-dashed">
          <thead>
            <tr>
              <th>Banco</th>
              <th>Número de Cuenta Tarjeta</th>
              <th>Número Cuenta Clabe</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <select
                  className="form-select"
                  name="account.bankId"
                  value={formik.values.account.bankId}
                  onChange={formik.handleChange}
                >
                  <option value="">Seleccione un banco</option>
                  {banks.map((bank) => (
                    <option key={bank.id} value={bank.id}>
                      {bank.description}
                    </option>
                  ))}
                </select>
                {formik.touched.account?.bankId && formik.errors.account?.bankId && (
                  <div className="fv-plugins-message-container text-danger w-100">
                    <span role="alert">{formik.errors.account?.bankId}</span>
                  </div>
                )}
              </td>
              <td>
                <input
                  type="text"
                  className="form-control"
                  name="account.name"
                  value={formik.values.account.name}
                  onChange={formik.handleChange}
                  placeholder="Numero de cuenta Tarjeta"
                />
                {formik.touched.account?.name && formik.errors.account?.name && (
                  <div className="fv-plugins-message-container text-danger w-100">
                    <span role="alert">{formik.errors.account?.name}</span>
                  </div>
                )}
              </td>
              <td>
                <input
                  type="text"
                  className="form-control"
                  name="account.account"
                  value={formik.values.account.account}
                  onChange={handleAccountInputChange}
                  placeholder="Numero cuenta clabe"
                />
                {formik.touched.account?.account && formik.errors.account?.account && (
                  <div className="fv-plugins-message-container text-danger w-100">
                    <span role="alert">{formik.errors.account?.account}</span>
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="d-flex justify-content-end mt-3">
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => formik.resetForm()}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            style={{marginRight: '2em'}}
            disabled={!collabId || loading}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>

      <h4 className="mt-5">Cuentas Guardadas</h4>
      <table className="table table-border table-row-dashed">
        <thead>
          <tr>
            <th>Banco</th>
            <th>Número de Cuenta Tarjeta</th>
            <th>Número Cuenta Clabe</th>
            <th>Seleccionar</th>
          </tr>
        </thead>
        <tbody>
          {savedAccounts.length === 0 ? (
            <tr>
              <td colSpan={4} className="text-center text-muted">
                {collabId
                  ? 'Comience por agregar una nueva cuenta.'
                  : 'Guarde el colaborador antes de agregar cuentas.'}
              </td>
            </tr>
          ) : (
            savedAccounts.map((account) => (
              <tr key={account.id}>
                <td>{account.bank}</td>
                <td>{account.name}</td>
                <td>{account.account}</td>
                <td>
                  <input
                    type="radio"
                    name="selectedAccount"
                    checked={account.statusId === 1}
                    onChange={() => handleRadioChange(account.id!)}
                    disabled={disabled}
                  />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export { DepositAccountsList };
